import { Suspense, useCallback, useEffect, useReducer, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/Button';
import { Text } from '@/components/ui/Text';
import { LoadingState } from '@/components/ui/StateViews';
import { GuestAuthPanel, type GuestAuthPanelState } from '@/components/guest/GuestAuthPanel';
import { getSession, postChallenge, postVerify, postLogout } from './guest-api';
import { safePortalPath } from './classify';
import { takeGuestToken, forgetGuestToken } from './capture-fragment';
import { GuestStepUpProvider, useGuestStepUp } from './step-up';
import type { GuestSessionState, LazyGuestPage } from './guest-types';

export interface GuestShellProps {
  registry: Readonly<Record<string, LazyGuestPage>>;
}

type ShellState =
  | { s: 'booting' }
  | { s: 'sending' }
  | { s: 'checkEmail'; error?: 'codeRejected' }
  | { s: 'verifying' }
  | { s: 'slot'; session: GuestSessionState }
  | { s: 'invalid' }
  | { s: 'wait' }
  | { s: 'unavailable'; retry: () => void }
  | { s: 'signedOut' }
  | { s: 'logoutFailed'; retry: () => void };

type Action =
  | { type: 'TO_SLOT'; session: GuestSessionState }
  | { type: 'TO_INVALID' }
  | { type: 'TO_WAIT' }
  | { type: 'TO_UNAVAILABLE'; retry: () => void }
  | { type: 'TO_SENDING' }
  | { type: 'TO_CHECK_EMAIL'; error?: 'codeRejected' }
  | { type: 'TO_VERIFYING' }
  | { type: 'TO_SIGNED_OUT' }
  | { type: 'TO_LOGOUT_FAILED'; retry: () => void };

function reduce(state: ShellState, action: Action): ShellState {
  switch (action.type) {
    case 'TO_SLOT':
      return { s: 'slot', session: action.session };
    case 'TO_INVALID':
      return { s: 'invalid' };
    case 'TO_WAIT':
      return { s: 'wait' };
    case 'TO_UNAVAILABLE':
      return { s: 'unavailable', retry: action.retry };
    case 'TO_SENDING':
      return { s: 'sending' };
    case 'TO_CHECK_EMAIL':
      return { s: 'checkEmail', error: action.error };
    case 'TO_VERIFYING':
      return { s: 'verifying' };
    case 'TO_SIGNED_OUT':
      return { s: 'signedOut' };
    case 'TO_LOGOUT_FAILED':
      return { s: 'logoutFailed', retry: action.retry };
    default:
      return state;
  }
}

/** `<tenantId>.<grantId>.<secret>` — the middle segment. */
function tokenGrantId(token: string): string | null {
  const parts = token.split('.');
  return parts.length === 3 ? parts[1] : null;
}

const MAX_VERIFY_REFUSALS = 3;

/** Maps every non-`slot` `ShellState` onto the `GuestAuthPanel`'s own state union. */
function toPanelState(
  state: Exclude<ShellState, { s: 'slot' }>,
  onSubmit: (code: string) => void,
  onResend: () => void,
): GuestAuthPanelState {
  switch (state.s) {
    case 'booting':
      return { tag: 'start' };
    case 'sending':
      return { tag: 'sending' };
    case 'checkEmail':
      return { tag: 'checkEmail', onSubmit, onResend, error: state.error };
    case 'verifying':
      return { tag: 'verifying' };
    case 'invalid':
      return { tag: 'invalid' };
    case 'wait':
      return { tag: 'wait' };
    case 'unavailable':
      return { tag: 'unavailable', onRetry: state.retry };
    case 'signedOut':
      return { tag: 'signedOut' };
    case 'logoutFailed':
      return { tag: 'logoutFailed', onRetry: state.retry };
  }
}

export function GuestShell({ registry }: GuestShellProps) {
  const [state, dispatch] = useReducer(reduce, { s: 'booting' });
  const queryClient = useQueryClient();
  const refusalCount = useRef(0);
  const tokenRef = useRef<string | null>(null);
  const bootedRef = useRef(false);
  // codex-qa round 3 (MR !504, C2): "Sign out" has no pending guard, so a double-click (or a
  // Retry racing a still-in-flight original attempt) can overlap two `handleLogout()` calls. Only
  // the LATEST attempt's response may update state; a straggling earlier one is ignored.
  const logoutAttemptRef = useRef(0);

  const sessionQuery = useQuery({ queryKey: ['guest', 'session'], queryFn: getSession });

  // Plain (non-memoized) functions, not useCallback — a useCallback whose own body calls itself
  // (the "unavailable" retry) is a forward self-reference that react-hooks/immutability rejects;
  // ordinary function declarations support recursion without that restriction. Re-created each
  // render is harmless here: callers (the boot effect, child props) tolerate a fresh identity.
  async function startChallenge(token: string) {
    dispatch({ type: 'TO_SENDING' });
    const answer = await postChallenge(token);
    if (answer.kind === 'sent') {
      // refusalCount is NOT reset here — it is already 0 on first boot, and a resend (which also
      // goes through this path) must not reset the 3-strike loop bound (DD Decision 10).
      dispatch({ type: 'TO_CHECK_EMAIL' });
    } else if (answer.kind === 'closed') {
      const safe = safePortalPath(answer.portalPath);
      if (safe) window.location.assign(safe);
      else dispatch({ type: 'TO_INVALID' });
    } else if (answer.kind === 'wait') {
      dispatch({ type: 'TO_WAIT' });
    } else if (answer.kind === 'unavailable') {
      dispatch({ type: 'TO_UNAVAILABLE', retry: () => void startChallenge(token) });
    } else {
      dispatch({ type: 'TO_INVALID' });
    }
  }

  useEffect(() => {
    if (bootedRef.current || sessionQuery.data === undefined) return;
    bootedRef.current = true;

    const token = takeGuestToken();
    tokenRef.current = token;
    const answer = sessionQuery.data;

    if (answer.kind === 'ok') {
      const grantId = token ? tokenGrantId(token) : null;
      const sameGrant = !!grantId && grantId.toLowerCase() === answer.data.grantId.toLowerCase();
      if (!token || sameGrant) {
        // codex-qa C5 (MR !504): forgetGuestToken() only clears the capture module's own copy —
        // the raw token also lives in tokenRef (set just above) and must be erased here too.
        if (token) { forgetGuestToken(); tokenRef.current = null; }
        dispatch({ type: 'TO_SLOT', session: answer.data });
        return;
      }
      // A live session for a DIFFERENT grant than the token — send exactly one challenge.
      void startChallenge(token);
      return;
    }

    if (token) {
      void startChallenge(token);
      return;
    }
    // No token: a refused session has no path forward (needs a fresh emailed link) → invalid.
    // `wait`/`unavailable` are transient backend conditions, not a dead link — collapsing them
    // into the permanent "link no longer valid" copy would tell the guest to request a new link
    // when retrying (or waiting) a moment later would have worked.
    if (answer.kind === 'wait') {
      dispatch({ type: 'TO_WAIT' });
    } else if (answer.kind === 'unavailable') {
      dispatch({
        type: 'TO_UNAVAILABLE',
        retry: () => {
          bootedRef.current = false;
          void sessionQuery.refetch();
        },
      });
    } else {
      dispatch({ type: 'TO_INVALID' });
    }
    // `startChallenge` is a fresh function every render (see above) and boot-only: `bootedRef`
    // guards re-entry, so re-running this effect on every render is a no-op after the first.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionQuery.data]);

  async function submitCode(code: string) {
    const token = tokenRef.current;
    if (!token) return;
    dispatch({ type: 'TO_VERIFYING' });
    const answer = await postVerify(token, code);
    if (answer.kind === 'ok') {
      refusalCount.current = 0;
      forgetGuestToken();
      tokenRef.current = null; // codex-qa C5 — erase the ref's own copy, not just the capture module's
      dispatch({ type: 'TO_SLOT', session: answer.data });
    } else if (answer.kind === 'wait') {
      dispatch({ type: 'TO_WAIT' });
    } else if (answer.kind === 'unavailable') {
      dispatch({ type: 'TO_UNAVAILABLE', retry: () => void submitCode(code) });
    } else {
      // refused — the 3-strike loop bound (DD Decision 10). A resend does not reset this.
      refusalCount.current += 1;
      if (refusalCount.current >= MAX_VERIFY_REFUSALS) {
        dispatch({ type: 'TO_INVALID' });
      } else {
        dispatch({ type: 'TO_CHECK_EMAIL', error: 'codeRejected' });
      }
    }
  }

  function resend() {
    const token = tokenRef.current;
    if (token) void startChallenge(token);
  }

  const handleSessionEnded = useCallback(() => {
    dispatch({ type: 'TO_INVALID' });
  }, []);

  // Plain function, not useCallback, for the same reason as `startChallenge` above: its own
  // retry action calls it again, a forward self-reference react-hooks/immutability rejects in a
  // useCallback body.
  async function handleLogout() {
    // ENT-566 decision (orchestrator/docs/prd/ent-566-task-first-f1.md:661; T08 amendment note):
    // local custody is erased immediately regardless of outcome, but `signedOut` is shown only
    // once the server confirms the session is actually gone.
    forgetGuestToken();
    tokenRef.current = null; // codex-qa C5 — erase the ref's own copy, not just the capture module's
    const attempt = ++logoutAttemptRef.current;
    const ok = await postLogout();
    if (attempt !== logoutAttemptRef.current) return; // superseded by a newer attempt — ignore
    if (ok) {
      queryClient.removeQueries({ queryKey: ['guest'] });
      dispatch({ type: 'TO_SIGNED_OUT' });
    } else {
      dispatch({ type: 'TO_LOGOUT_FAILED', retry: () => void handleLogout() });
    }
  }

  const slotHeadingRef = useRef<HTMLElement>(null);
  // AC-P0d.F10: every transition focuses the new heading and updates the polite live region — the
  // slot is the one `ShellState` with no `GuestAuthPanel` (which already does this for every other
  // state), so it needs its own heading + focus effect rather than inheriting one.
  useEffect(() => {
    if (state.s === 'slot') slotHeadingRef.current?.focus();
  }, [state.s]);

  if (state.s === 'slot') {
    return (
      <GuestStepUpProvider onRefused={handleSessionEnded}>
        <main className="min-h-screen bg-bg flex flex-col">
          <Text as="h1" variant="title-md" tabIndex={-1} aria-live="polite" ref={slotHeadingRef} className="sr-only">
            You&apos;re signed in
          </Text>
          <div className="flex justify-end p-4">
            <Button variant="ghost" onClick={() => void handleLogout()}>
              Sign out
            </Button>
          </div>
          <div className="flex-1 flex items-center justify-center p-4">
            <GuestProductSlot
              registry={registry}
              session={state.session}
              onSessionEnded={handleSessionEnded}
            />
          </div>
        </main>
      </GuestStepUpProvider>
    );
  }

  // codex-qa round 8 (T0297, finding 2 / F4) — axe landmark-one-main/region: the challenge and
  // refusal states (checkEmail, invalid, wait, unavailable, signedOut, logoutFailed) rendered a
  // bare <div>, so the code input and refusal copy sat outside any landmark. A <main> here covers
  // every non-slot state from one place, same as the slot branch above.
  return (
    <main className="min-h-screen bg-bg flex items-center justify-center p-4">
      <GuestAuthPanel state={toPanelState(state, submitCode, resend)} />
    </main>
  );
}

interface GuestProductSlotProps {
  registry: Readonly<Record<string, LazyGuestPage>>;
  session: GuestSessionState;
  onSessionEnded: () => void;
}

/**
 * Renders the registered product page for `session.resourceKind`. An unknown `resourceKind` (no
 * matching registry entry) ends the session the same way a refused product-route call does.
 */
function GuestProductSlot({ registry, session, onSessionEnded }: GuestProductSlotProps) {
  const requestStepUp = useGuestStepUp();
  const Page = registry[session.resourceKind];

  useEffect(() => {
    if (!Page) onSessionEnded();
  }, [Page, onSessionEnded]);

  if (!Page) return null;

  return (
    <Suspense fallback={<LoadingState label="One moment…" />}>
      <Page session={session} requestStepUp={requestStepUp} reportSessionEnded={onSessionEnded} />
    </Suspense>
  );
}
